import type { ReactElement } from 'react'

import type { ArtifactKind, ArtifactRecord } from '../../../types'
import {
  BulkEditPreviewArtifact,
  type BulkEditPreviewSpec,
  CaseFactPreviewArtifact,
  type CaseFactPreviewSpec,
  ChartArtifact,
  type ChartSpec,
  ChecksArtifact,
  type ChecksSpec,
  ChoicesArtifact,
  type ChoicesSpec,
  DownloadArtifact,
  type DownloadSpec,
  MapArtifact,
  type MapSpec,
  RemoveTransactionsPreviewArtifact,
  type RemoveTransactionsPreviewSpec,
  ReportEditPreviewArtifact,
  type ReportEditPreviewSpec,
  TableArtifact,
  type TableSpec,
  TransactionsArtifact,
  type TransactionsSpec,
} from '../artifacts'

interface ArtifactViewProps {
  artifact: ArtifactRecord
  slug: string
}

type ArtifactRenderer = (
  spec: unknown,
  slug: string,
  toolUseId: string,
) => ReactElement

// Keyed on the registry-derived ArtifactKind union, so adding a kind to
// the registry without a renderer here is a compile error.
const RENDERERS: Record<ArtifactKind, ArtifactRenderer> = {
  chart: (spec) => <ChartArtifact spec={spec as ChartSpec} />,
  table: (spec) => <TableArtifact spec={spec as TableSpec} />,
  transactions: (spec) => (
    <TransactionsArtifact spec={spec as TransactionsSpec} />
  ),
  checks: (spec) => <ChecksArtifact spec={spec as ChecksSpec} />,
  map: (spec) => <MapArtifact spec={spec as MapSpec} />,
  download: (spec) => <DownloadArtifact spec={spec as DownloadSpec} />,
  bulk_edit_preview: (spec, slug) => (
    <BulkEditPreviewArtifact slug={slug} spec={spec as BulkEditPreviewSpec} />
  ),
  remove_preview: (spec, slug) => (
    <RemoveTransactionsPreviewArtifact
      slug={slug}
      spec={spec as RemoveTransactionsPreviewSpec}
    />
  ),
  report_edit_preview: (spec, slug) => (
    <ReportEditPreviewArtifact
      slug={slug}
      spec={spec as ReportEditPreviewSpec}
    />
  ),
  case_fact_preview: (spec, slug) => (
    <CaseFactPreviewArtifact slug={slug} spec={spec as CaseFactPreviewSpec} />
  ),
  choices: (spec, slug, toolUseId) => (
    <ChoicesArtifact
      slug={slug}
      spec={spec as ChoicesSpec}
      toolUseId={toolUseId}
    />
  ),
}

export default function ArtifactView({ artifact, slug }: ArtifactViewProps) {
  return RENDERERS[artifact.kind](artifact.spec, slug, artifact.toolUseId)
}
